/**
 * **Distancias sobre la Tierra, sin extensiones de PostgreSQL.**
 *
 * Vive en `core` y no en la app de consumo porque lo usan las dos puntas: la vitrina que ordena tiendas
 * por cercanía y la app que deduce el municipio de un punto. Dos copias de una fórmula son dos formas
 * de que un día el mismo par de coordenadas dé dos distancias distintas.
 *
 * `cube` + `earthdistance` están disponibles en el servidor pero **sin instalar**, y activar una
 * extensión toca la base que comparten las 10 apps. Para un puñado de tiendas no se paga. El día que
 * haya miles, esto pasa a `earth_box` con índice GiST — y se notará, porque el orden por distancia
 * empezará a tardar.
 *
 * ⚠️ Este archivo **no importa `db`** y no debe hacerlo: solo arma texto SQL, así que puede usarse desde
 * cualquier sitio sin arrastrar `pg` al navegador (regla dura de este repo).
 */

/**
 * Fragmento SQL que da la distancia en **kilómetros** entre dos puntos. 6371 es el radio medio de la
 * Tierra; el `LEAST/GREATEST` protege del error de redondeo que puede sacar a `acos` de su dominio y
 * devolver `NaN` cuando los dos puntos son el mismo.
 *
 * ⚠️ **Cada argumento se castea a `float8`, y no es adorno.** Las columnas son `numeric` y `radians()`
 * solo existe para `double precision`. Sin el casteo, un parámetro usado a la vez para guardar en una
 * columna `numeric` y dentro de la fórmula hace que Postgres deduzca dos tipos para él y rechace la
 * consulta con *"inconsistent types deduced for parameter $N"*. Pasó en vivo el 2026-09-08, y **no lo
 * detectó una prueba previa en `psql`**: allí los tipos iban declarados a mano en el `PREPARE`, que es
 * justo lo que el driver no hace.
 */
export function sqlDistanciaKm(latA: string, lonA: string, latB: string, lonB: string): string {
  const f = (x: string) => `(${x})::float8`;
  return `(6371 * acos(LEAST(1, GREATEST(-1,
      cos(radians(${f(latA)})) * cos(radians(${f(latB)})) * cos(radians(${f(lonB)}) - radians(${f(lonA)}))
    + sin(radians(${f(latA)})) * sin(radians(${f(latB)}))
  ))))`;
}

/**
 * "a 800 m" / "a 1,2 km". **Nunca un número crudo**: lo que se muestra es la distancia, jamás la
 * coordenada del otro. Por debajo de 1 km se redondea a 50 m — dar "a 812 m" finge una precisión que
 * ni el GPS de un celular tiene, y de paso dice más de lo necesario sobre dónde vive alguien.
 */
export function distanciaTexto(km: number | null | undefined): string | null {
  if (km === null || km === undefined || Number.isNaN(km)) return null;
  if (km < 1) return `a ${Math.max(50, Math.round((km * 1000) / 50) * 50)} m`;
  return `a ${km.toFixed(1).replace(".", ",")} km`;
}
